import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Visibility
} from '@mui/icons-material';
import { apiService } from '../../config/api';

interface Application {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  institution: string;
  status: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewNotes?: string;
  description?: string;
  dateOfBirth: string;
  verificationDocuments?: Array<{
    name: string;
    size: number;
    type: string;
    data: string;
  }>;
}

interface RejectedApplicationsProps {
  onApplicationProcessed: () => void;
}

const RejectedApplications: React.FC<RejectedApplicationsProps> = ({ onApplicationProcessed }) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/api/admin/researcher-applications?status=rejected');
      
      if (response.success) {
        setApplications(response.applications || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load rejected applications');
    } finally {
      setLoading(false);
    }
  };


  const handleViewApplication = (application: Application) => {
    setSelectedApplication(application);
    setViewDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {applications.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            No Rejected Applications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All applications are either pending or approved.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Applicant</TableCell>
                <TableCell>Institution</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rejected Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow key={application.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {application.firstName} {application.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {application.email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.institution}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {application.description || 'No description provided'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={application.status.toUpperCase()}
                      color={getStatusColor(application.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {application.reviewedAt ? formatDate(application.reviewedAt) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small"
                        onClick={() => handleViewApplication(application)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* View Application Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Application Details - {selectedApplication?.firstName} {selectedApplication?.lastName}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedApplication.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Institution
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedApplication.institution}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date of Birth
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(selectedApplication.dateOfBirth).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={selectedApplication.status}
                  color={getStatusColor(selectedApplication.status) as any}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Submitted
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {formatDate(selectedApplication.submittedAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Rejected
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {selectedApplication.reviewedAt ? formatDate(selectedApplication.reviewedAt) : 'N/A'}
                </Typography>
              </Grid>
              {selectedApplication.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedApplication.description}
                  </Typography>
                </Grid>
              )}
              {selectedApplication.reviewNotes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Review Notes
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedApplication.reviewNotes}
                  </Typography>
                </Grid>
              )}
              {selectedApplication.verificationDocuments && selectedApplication.verificationDocuments.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Verification Documents
                  </Typography>
                  {selectedApplication.verificationDocuments.map((doc, index) => (
                    <Typography key={index} variant="body2" color="text.secondary">
                      • {doc.name} ({(doc.size / 1024).toFixed(1)} KB)
                    </Typography>
                  ))}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RejectedApplications;
